import { useEffect, useState } from "react";
import { calculateEtaInMins } from "@/utils/train-eta";
import { getBlobDurationInSecs } from "@/utils/blob-utils";
import { getActiveLoggingService } from "@/services/logger/logging-service";
import { TrainQualityPresetType } from "@/services/license/license-limiters/service-quality-limiters/custom-voice-quality-limiter";
import { customVoiceLicenseManager } from "@/services/license/license-managers/custom-voices-license-manager";
import { useProfileContext } from "@/providers/profile-provider/profile-provider";
import { showCustomVoiceQualityNowSupportedError } from "@/utils/notification-helper";

type StartTrainResult = {
  type: "success" | "error";
  message: string;
};

export type TrainQualityPreset = {
  title: string;
  epochs: number;
  type: TrainQualityPresetType;
};

const useCreateCustom = () => {
  const presets: TrainQualityPreset[] = [
    { title: "Low Quality", epochs: 100, type: "low" },
    { title: "Default Quality", epochs: 200, type: "default" },
    {
      title: "Highest Quality",
      epochs: 350,
      type: "highest",
    },
  ];

  const { user, license } = useProfileContext();

  const [datasetFiles, setDatasetFiles] = useState<File[]>([]);
  const [voiceName, setVoiceName] = useState<string>("Voice");
  const [datasetLanguage, setDatasetLanguage] = useState<string>("English");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedPreset, setSelectedPreset] = useState(presets[1]);
  const [eta, setEta] = useState<number>();

  const changeVoiceName = (value: string) => {
    setVoiceName(value.replace(/\s+/g, " "));
  };

  const setPreset = async (preset: TrainQualityPreset) => {
    const { isQualityAllowed } =
      await customVoiceLicenseManager.serviceQualityLimiter.isQualityAllowed(
        user?.id,
        license,
        preset.type,
      );

    if (isQualityAllowed) {
      setSelectedPreset(preset);
    } else {
      showCustomVoiceQualityNowSupportedError();
    }
  };

  useEffect(() => {
    const recalculateEta = async () => {
      if (datasetFiles.length === 0) {
        setEta(undefined);
        return;
      }

      let totalLength = 0;
      let errorsCount = 0;

      for (const file of datasetFiles) {
        totalLength += await getBlobDurationInSecs(file).catch((e) => {
          errorsCount++;
          return 0;
        });
      }

      if (errorsCount === datasetFiles.length) {
        setEta(undefined);
        return;
      }

      setEta(
        calculateEtaInMins(
          totalLength,
          datasetFiles.length,
          selectedPreset.epochs,
        ),
      );
    };

    recalculateEta();
  }, [datasetFiles.length, selectedPreset]);

  useEffect(() => {
    const checkPreset = async () => {
      const { isQualityAllowed, maxQuality } =
        await customVoiceLicenseManager.serviceQualityLimiter.isQualityAllowed(
          user?.id,
          license,
          selectedPreset.type,
        );

      if (!isQualityAllowed) {
        let preset = presets.find((preset) => preset.type === maxQuality);

        if (!preset) {
          preset = presets[0];
        }

        setSelectedPreset(preset);
      }
    };

    checkPreset();
  }, [user, license]);

  const startTrainVoice = async (): Promise<StartTrainResult> => {
    setIsLoading(true);
    const formData = new FormData();
    datasetFiles.forEach((file) => {
      formData.append("dataset", file);
    });
    formData.append("name", voiceName.trim());
    formData.append("datasetLanguage", datasetLanguage);
    formData.append("epochs", selectedPreset.epochs.toString());

    try {
      const response = await fetch("/api/train/start/", {
        method: "POST",
        body: formData,
      });

      if (response.status >= 500) {
        setIsLoading(false);
        return {
          type: "error",
          message: "Something went wrong",
        };
      }

      if (!response.ok) {
        const data = await response.json();
        let message = "Something went wrong";
        switch (data.code) {
          case "invalid-dataset":
            message = "Dataset is invalid";
            break;
          case "missing-name":
            message = "Voice Name is missing";
            break;
          case "missing-language":
            message = "Dataset language is missing";
            break;
        }

        setIsLoading(false);

        return {
          type: "error",
          message,
        };
      }
    } catch (error) {
      getActiveLoggingService().logError(error as Error);
      setIsLoading(false);
      return {
        type: "error",
        message: "Something went wrong",
      };
    }

    setIsLoading(false);

    return {
      type: "success",
      message: "",
    };
  };

  return {
    datasetFiles: datasetFiles,
    presets,
    selectedPreset,
    setSelectedPreset: setPreset,
    changeDatasetFiles: setDatasetFiles,
    datasetLanguage: datasetLanguage,
    changeDatasetLanguage: setDatasetLanguage,
    voiceName: voiceName,
    changeVoiceName,
    startTrainVoice: startTrainVoice,
    isLoading: isLoading,
    eta: eta,
  };
};

export default useCreateCustom;
